import { takeLatest, put, all } from 'redux-saga/effects';
import { push } from 'connected-react-router';
import config from 'config';

import { disableGoogleAutoSelect } from './helpers';
import { identifyUser, resetAnalyticsUser, track } from 'analytics/posthog';
import { clearAdminSession } from 'containers/Admin/adminSession';
import * as actions from './actions';

// -- Login User -- //
function* loginUser(action) {
  try {
    // Store user data in browser's localStorage
    localStorage.setItem('authorization-token', action.data.tokenId);
    localStorage.removeItem('permission-token');
    if (action.data.tokenObj)
      localStorage.setItem('token', JSON.stringify(action.data.tokenObj));
    else localStorage.removeItem('token');
    localStorage.setItem('user', JSON.stringify(action.data.profileObj));
    localStorage.setItem('tokenType', action.data.tokenType);
    localStorage.removeItem('jwt_refresh_count');
    localStorage.setItem('umsId', action.data.umsId || '');
    clearAdminSession();
    document.cookie = `authorization-token=${action.data.tokenId}; path=/; secure`;

    try {
      const serverUrl = String(config.serverUrl || '').replace(/\/$/, '');
      const response = yield fetch(`${serverUrl}/api/v1/permissions/token`, {
        method: 'POST',
        headers: { 'authorization-token': action.data.tokenId },
      });
      if (!response.ok) {
        throw new Error(`Permission token request failed with status ${response.status}`);
      }

      const { permissionToken } = yield response.json();
      if (typeof permissionToken !== 'string' || !permissionToken) {
        throw new Error('Permission token response was invalid');
      }
      localStorage.setItem('permission-token', permissionToken);
    } catch (error) {
      // Authentication succeeded, so keep the normal login flow working if
      // permission token issuance is temporarily unavailable.
      console.error('Failed to retrieve permission token after login', error);
    }

    identifyUser({
      umsId: action.data.umsId,
      id: action.data.profileObj?.googleId,
      email: action.data.profileObj?.email,
      name: action.data.profileObj?.name,
      type: action.data.profileObj?.type,
    });
    track('login succeeded', {
      login_method: action.data.tokenType,
      user_type: action.data.profileObj?.type,
      ums_id_present: Boolean(action.data.umsId),
      surface: 'login',
    });

    // Place user information into the session slice of state
    yield put(actions.receiveUser(action.data));
    // Redirect browser to the requested page or homepage following login
    const path = action.data.referrer || '/';
    yield put(push(path));
  } catch (error) {
    track('login failed', {
      login_method: action.data?.tokenType || 'unknown',
      error_code_or_message: String(error?.message || error || 'unknown'),
      surface: 'login',
    });
    yield put(actions.receiveUserError(error));
  }
}
// -- End Login User -- //

// -- Logout User -- //
function* logoutUser() {
  try {
    track('logout clicked', {
      surface: 'header',
    });
    const authStorageKeys = [
      'token',
      'user',
      'umsId',
      'authorization-token',
      'permission-token',
      'tokenType',
      'jwt_refresh_count',
    ];
    // Remove token and user data from localStorage
    authStorageKeys.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    clearAdminSession();
    document.cookie =
      'authorization-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; max-age=0; secure';
    disableGoogleAutoSelect();
    resetAnalyticsUser();
    // Alter session state to denote logged out state
    yield put(actions.logoutUserSuccess());
    // Redirect browser to login page following logout
    yield put(push('/login'));
  } catch (error) {
    yield put(actions.logoutUserError(error));
  }
}

// -- End Logout User -- //

// export all related sagas
export default function* rootSaga() {
  yield all([
    takeLatest(actions.LOGIN_USER, loginUser),
    takeLatest(actions.LOGOUT_USER, logoutUser),
  ]);
}
